<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import { useBroadcastStore } from '@/stores/broadcast'

const broadcastStore = useBroadcastStore()

const contentRef = ref<HTMLElement | null>(null)

const visible = computed({
  get: () => broadcastStore.visible,
  set: (value) => {
    if (!value) broadcastStore.close()
  },
})

const current = computed(() => broadcastStore.current)

const nextButtonText = computed(() => {
  if (broadcastStore.isLast) return `完成(${broadcastStore.pageLabel})`
  return `下一条(${broadcastStore.pageLabel})`
})

function handleNextOrComplete() {
  if (broadcastStore.isLast) {
    broadcastStore.close()
  } else {
    broadcastStore.goNext()
  }
}

watch(
  () => broadcastStore.currentIndex,
  async () => {
    await nextTick()
    contentRef.value?.scrollTo({ top: 0 })
  },
)
</script>

<template>
  <van-popup
    v-model:show="visible"
    position="bottom"
    round
    safe-area-inset-bottom
    :close-on-click-overlay="false"
    teleport="body"
  >
    <div v-if="current" class="flex flex-col bg-white">
      <!-- 标题栏 -->
      <div
        class="relative flex h-12 shrink-0 items-center justify-center border-b border-border-light"
      >
        <h3 class="m-0 text-base font-semibold text-text-primary">{{ current.title }}</h3>
        <button
          type="button"
          class="absolute right-3 top-1/2 flex size-8 -translate-y-1/2 items-center justify-center"
          aria-label="关闭"
          @click="broadcastStore.close"
        >
          <van-icon name="cross" size="18" color="#969799" />
        </button>
      </div>

      <!-- 富文本内容 -->
      <div
        ref="contentRef"
        class="content-wrapper min-h-[116px] max-h-[40vh] overflow-y-auto px-4 py-3 text-sm leading-relaxed text-text-secondary wrap-break-word [-webkit-overflow-scrolling:touch]"
        v-html="current.content"
      />

      <!-- 底部按钮 -->
      <div class="shrink-0 border-t border-border-light px-4 py-3">
        <div v-if="broadcastStore.hasMultiple" class="flex gap-3">
          <van-button
            plain
            class="h-11 flex-1 rounded-lg! border-[#dcdee0]! text-[#646566]!"
            :disabled="broadcastStore.isFirst"
            @click="broadcastStore.goPrev"
          >
            上一条
          </van-button>
          <van-button type="primary" class="h-11 flex-1 rounded-lg!" @click="handleNextOrComplete">
            {{ nextButtonText }}
          </van-button>
        </div>
        <van-button
          v-else
          type="primary"
          block
          class="h-11 rounded-lg!"
          @click="broadcastStore.close"
        >
          我知道了
        </van-button>
      </div>
    </div>
  </van-popup>
</template>

<style lang="less" scoped>
.content-wrapper {
  :deep(img) {
    display: block;
    width: 100%;
    height: auto;
    margin: 0;
  }

  :deep(table) {
    width: 100% !important;
    border-collapse: collapse;
    border-spacing: 0;
    border: 1px solid #e0e0e0;

    th {
      background-color: #f5f5f5;
    }

    th,
    td {
      border: 1px solid #e0e0e0;
      text-align: center;
    }
  }
}
</style>
